/// <reference types="@types/google.maps" />
import { ConsolidatedPlotCurrentStatusEnum } from '#backend/entity/loaded/harvest-monitoring/ConsolidatedPlotCurrentStatus';
import { MonitoringArea } from '#backend/entity/loaded/harvest-monitoring/MonitoringArea';
import type { MonitoredPlotMvtDto } from '#backend/mvt-layers/MonitoredPlotMvtDto';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  resource,
  signal,
} from '@angular/core';
import { Color, PickingInfo, type DeckProps } from '@deck.gl/core';
import { LoadingWidget } from '@deck.gl/widgets';
import { MVTLayer } from '@deck.gl/geo-layers';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import * as gMapsApiLoader from '@googlemaps/js-api-loader';
import { environment } from '../../../../../environments/environment';
import { SmartOrpcClientService } from '../../../../shared/services/smart-orpc-client.service';
import { defaultLayerProps } from '../../../../shared/utils/layerProps';
import {
  getPlotStatusName,
  getStatusColorRgb,
} from '../../../../shared/utils/monitoredPlotStatus';
import { UserService } from '../../../../shared/services/user.service';

const PLOT_LAYER_ID = 'monitored-plots-layer';
const MONIT_LAYER_ID = 'monitored-areas-polygon-layer';
const WGS84_SRID = 4326;

@Component({
  selector: 'app-plots-map',
  imports: [],
  templateUrl: './plots-map.component.html',
  styleUrl: './plots-map.component.scss',
})
export class PlotsMapComponent implements OnInit {
  gMap = signal<google.maps.Map | null>(null);
  public readonly gmapElementId = 'gmap';
  monitAreaIds = input<number[]>([]);
  selectedPlotId = input<number | null>(null);
  cultureName = input<string | undefined>();
  plotClick = output<number>();
  monitAreaURL = computed(() => this.getMonitAreaURL(this.monitAreaIds()));
  plotsAreaURL = computed(() => this.getPlotsUrl(this.monitAreaIds()));
  orpc = inject(SmartOrpcClientService);
  userService = inject(UserService);

  layers = computed(() => {
    const monitUrl = this.getMonitAreaURL(this.monitAreaIds());
    const plotsUrl = this.getPlotsUrl(this.monitAreaIds());
    const selectedPlotId = this.selectedPlotId();

    if (monitUrl == null || plotsUrl == null) return [];
    return [
      this.getPlotsLayer(plotsUrl, selectedPlotId),
      this.getMonitAreaLayer(monitUrl),
    ];
  });

  overlay = new GoogleMapsOverlay({
    layers: this.layers(),
    widgets: [new LoadingWidget({ placement: 'top-right' })],
    getTooltip: (info) => this.getTooltip(info),
    interleaved: false, // se for true,  causa serrilhamento nas geometrias tipo linha
  });

  readonly monitAreaEnvelope = resource({
    params: () => ({ ids: this.monitAreaIds() }),
    loader: async ({ params }) => {
      const { error, data } =
        await this.orpc.safeClient.monitoring.areas.envelopeFromIdList({
          monitoringAreaIds: params.ids,
        });
      if (error) {
        return;
      }
      return data;
    },
  });

  constructor() {
    effect(() => {
      const layers = this.layers();
      this.renderLayers(layers);
    });

    effect(() => {
      const monitAreaEnvelope = this.monitAreaEnvelope.value();
      const gMap = this.gMap();

      if (monitAreaEnvelope && gMap) {
        const { bbox, crs } = monitAreaEnvelope;
        if (bbox && crs?.properties.name === `EPSG:${WGS84_SRID}`) {
          gMap.fitBounds(this.toGoogleBounds(bbox));
        }
      }
    });
  }

  private getMonitAreaURL(monitIds: number[]) {
    if (monitIds === null) return null;
    const params = new URLSearchParams({
      monitAreaIds: JSON.stringify(monitIds),
    });
    return `${environment.MARTIN_PROTOCOL}://${environment.MARTIN_HOST}/get_monit_area_polygon_mvt/{z}/{x}/{y}?${params}`;
  }

  private getPlotsUrl(monitIds: number[]) {
    if (monitIds === null) return null;
    const params = new URLSearchParams({
      monitAreaIds: JSON.stringify(monitIds),
    });
    return `${environment.MARTIN_PROTOCOL}://${environment.MARTIN_HOST}/get_consolidated_plot_mvt/{z}/{x}/{y}?${params}`;
  }

  private getPlotsLayer(url: string, selectedPlotId: number | null) {
    return new MVTLayer<MonitoredPlotMvtDto>({
      ...defaultLayerProps,
      id: PLOT_LAYER_ID,
      data: [url],
      uniqueIdProperty: 'id',
      binary: false, // binary:true zera propriedades numéricas ausentes (ex: zarc_risk nulo)
      updateTriggers: {
        getLineColor: selectedPlotId,
        getLineWidth: selectedPlotId,
      },

      transitions: {
        getLineColor: 300,
        getLineWidth: 300,
      },

      onHover: (info) => {
        this.gMap()?.setOptions({
          draggableCursor:
            info.object != null && this.isPlotPickingInfo(info)
              ? 'pointer'
              : 'default',
        });
      },

      onClick: (info) => {
        if (this.isPlotPickingInfo(info)) {
          const id = info.object?.properties.id;
          if (id != null) {
            this.plotClick.emit(id);
          }
        }
      },

      getLineColor: (f) => {
        if (selectedPlotId != null && f.properties.id === selectedPlotId)
          return [255, 0, 0]; // vermelho
        return [156, 156, 156]; // cinza
      },

      getFillColor: (f) => this.getStatusColor(f.properties.status),

      lineWidthUnits: 'pixels',
      getLineWidth: (f) => (f.properties.id == selectedPlotId ? 6 : 3),

      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 128],
    });
  }

  private getMonitAreaLayer(url: string) {
    return new MVTLayer<MonitoringArea>({
      ...defaultLayerProps,
      id: MONIT_LAYER_ID,
      uniqueIdProperty: 'id',
      data: [url],
      getLineColor: () => [255, 255, 115], // amarelo
      getFillColor: () => [0, 0, 0, 0], // transparente
      lineWidthUnits: 'pixels',
      getLineWidth: 3,
      pickable: false,
      autoHighlight: false,
      highlightColor: [255, 255, 255, 128],
    });
  }

  private isPlotPickingInfo(
    info: PickingInfo,
  ): info is PickingInfo<
    GeoJSON.Feature<GeoJSON.Polygon, MonitoredPlotMvtDto>
  > {
    return info.layer?.id === PLOT_LAYER_ID;
  }

  getStatusColor(s: ConsolidatedPlotCurrentStatusEnum | null): Color {
    if (s == null) return [0, 0, 0, 0];
    const rgb = getStatusColorRgb(s);
    return [...rgb, 200];
  }

  ngOnInit(): void {
    this.loadGMap();
  }

  renderLayers(layers: MVTLayer[]) {
    this.overlay.setProps({
      layers,
    });
  }

  async loadGMap() {
    gMapsApiLoader.setOptions({
      key: environment.GOOGLE_API_KEY,
    });
    try {
      const gMapsLib = await gMapsApiLoader.importLibrary('maps');

      const mapEl = document.getElementById(this.gmapElementId)!;
      this.gMap.set(
        new gMapsLib.Map(mapEl, {
          gestureHandling: 'greedy',
          fullscreenControl: false,
          zoomControl: true,
          streetViewControl: false,
          scaleControl: true,
          renderingType: google.maps.RenderingType.RASTER,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          mapTypeControlOptions: {
            mapTypeIds: [
              google.maps.MapTypeId.ROADMAP,
              google.maps.MapTypeId.TERRAIN,
              google.maps.MapTypeId.SATELLITE,
            ],
          },
          clickableIcons: false,
          isFractionalZoomEnabled: true,
          styles: [],
        }),
      );
      this.overlay.setMap(this.gMap());
    } catch (error) {
      console.error(error);
    }
  }
  // Callback to populate the default tooltip with content
  //
  getTooltip(
    info: PickingInfo<unknown>,
  ): ReturnType<NonNullable<DeckProps['getTooltip']>> {
    if (info.coordinate === undefined) return null;
    const [coordX, coordY] = info.coordinate.map((n) =>
      n.toLocaleString(undefined, {
        maximumFractionDigits: 5,
        minimumFractionDigits: 5,
      }),
    );
    const [lat, long] = [coordY, coordX];
    const coords = /*html*/ `<span
      style="font-variant-numeric: tabular-nums;"
    >Lat: ${lat}, Long: ${long}</span>`;

    function getZarcBadgeHtml(
      zarcRisk: number | null,
      zarcCalculatedAt: string | null,
    ) {
      const colors: Record<
        number | 'out',
        { bg: string; color: string; border: string }
      > = {
        20: { bg: '#e6f4ea', color: '#1e7e34', border: '#a8d5b5' },
        30: { bg: '#fff8e1', color: '#b45309', border: '#fcd34d' },
        40: { bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a' },
        out: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
      };
      const badgeStyle = (k: number | 'out') => {
        const c = colors[k] ?? colors['out'];
        return `
        line-height: 1.0;
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: bold;
        background-color: ${c.bg};
        color: ${c.color};
        border: 1px solid ${c.border};
        `;
      };
      if (zarcRisk != null) {
        return /*html*/ `<span
        style="${badgeStyle(zarcRisk)}"
      >${zarcRisk}%</span>`;
      }

      if (zarcCalculatedAt != null) {
        // Cálculo executado mas sem correspondência no ZARC
        return /*html*/ `<span
        style="${badgeStyle('out')}"
      ><i>Fora do Zoneamento</i></span>`;
      }

      return '-';
    }

    if (this.isPlotPickingInfo(info) && info.object) {
      const props = info.object.properties;
      const rgba = this.getStatusColor(props.status).join(',');
      const displayPlotId = this.userService.isGeoambiente ? 'revert' : 'none';
      const displayZarc =
        props.zarc_risk != null || props.zarc_calculated_at != null
          ? 'revert'
          : 'none';
      return {
        html: /*html*/ `<table>
        <tr><td><b>Coordenadas: </b></td><td>${coords}</td></tr>
        <tr style="display: ${displayPlotId}"><td><b>ID Talhão: </b></td><td>${props.id}</td></tr>
        <tr><td><b>Status: </b></td><td><span
          style="color: rgba(${rgba}); font-size: 180%; line-height: 0;"
        >&#9632</span> ${getPlotStatusName(props.status, this.cultureName()) ?? 'Desconhecido'}</td></tr>
        <tr><td><b>Área: </b></td><td>${Number(props.area_ha).toLocaleString(
          undefined,
          { maximumFractionDigits: 2 },
        )} ha</td></tr>
        <tr style="display: ${displayZarc}"><td><b>ZARC: </b></td><td>${getZarcBadgeHtml(
          props.zarc_risk,
          props.zarc_calculated_at,
        )}</td></tr>
        </table>
        `,
        style: {
          fontSize: '14px',
          backgroundColor: '#212a06cc',
          backdropFilter: 'blur(4px)',
          color: 'white',
          marginLeft: '15px',
        },
      };
    }
    return {
      html: `<b>Coordenadas:</b> ${coords}`,
      style: {
        marginLeft: '15px',
      },
    };
  }

  toGoogleBounds(geojsonBounds: GeoJSON.BBox): google.maps.LatLngBoundsLiteral {
    const bounds = {
      west: geojsonBounds[0],
      north: geojsonBounds[1],
      east: geojsonBounds[2],
      south: geojsonBounds[3],
    };
    return bounds;
  }
}
